import { useState } from "react";
import { Trash2 } from "lucide-react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { SectionCard } from "@/features/power_projects/components/ui";
import { useDeleteLeadFile, useUploadLeadFile, type Lead } from "@/features/leads/api/leads";
import { uploadErrorMessage } from "@/utils/managedFile";

/**
 * Pliki na karcie firmy — cennik, referencje, uprawnienia, polisa.
 *
 * To nie są formalne oferty (te żyją w postępowaniach ofertowych), tylko to, co
 * firma przysłała, zanim została podwykonawcą.
 */

export function LeadAttachmentsPanel({ lead }: { lead: Lead }) {
  const upload = useUploadLeadFile();
  const remove = useDeleteLeadFile();
  const [error, setError] = useState("");

  // One file after another: with mutate() per file only the LAST call's onError
  // fires (TanStack v5), so a failure of an earlier file went unreported.
  const handle = async (files: File[]) => {
    setError("");
    const failed: string[] = [];
    for (const file of files) {
      try {
        await upload.mutateAsync({ leadId: lead.id, file });
      } catch (err) {
        failed.push(uploadErrorMessage(err, `Nie udało się wgrać pliku ${file.name}.`));
      }
    }
    if (failed.length) setError(failed.join(" "));
  };

  return (
    <SectionCard title="Pliki">
      <FileDropZone
        multiple
        onSelect={(files) => void handle(files)}
        disabled={upload.isPending}
        label="Przeciągnij pliki albo kliknij, żeby wybrać"
        className="mb-3"
      />
      {error && <p className="mb-2 text-xs text-[var(--color-primary)]">{error}</p>}

      {lead.attachments.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">Brak plików.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {lead.attachments.map((file) => (
            <li key={file.id} className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <ManagedFileLink
                  file={{
                    url: file.url,
                    scan_status: file.scan_status,
                    original_filename: file.original_filename,
                  }}
                >
                  {file.label || file.original_filename}
                </ManagedFileLink>
                <div className="text-xs text-[var(--color-text-muted)]">
                  {file.created_by_name ?? "—"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove.mutate(file.id)}
                className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                title="Usuń plik"
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
